import React, { useEffect, useState } from "react";
import { Trash2, Upload } from "lucide-react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import toast from "react-hot-toast";

import { selectCurrentUser } from "../../redux/user/user.selectors";
import { sessionExpired, updateToken } from "../../redux/user/user.actions";
import { authFetch, getApiUrl, SessionExpiredError } from "../../utils/api";

const FileUpload = ({ currentUser, onSessionExpired, onTokenRefresh }) => {
    const [isDragging, setIsDragging] = useState(false);
    const [userLogo, setUserLogo] = useState(null);

    const apiUrl = getApiUrl();
    const authParams = { currentUser, onSessionExpired, onTokenRefresh };

    const getUserDetails = async () => {
        try {
            const response = await authFetch(
                `${apiUrl}/proposals/getuserdetails/${currentUser.id}`,
                { method: 'GET' },
                authParams
            );

            if (response.ok) {
                const data = await response.json();
                if (data.userDetails) {
                    setUserLogo(data.userDetails.customLogo);
                }
            }
            else {
                console.error("HTTP Error:", response.status, response.statusText);
            }
        } catch (err) {
            if (!(err instanceof SessionExpiredError)) {
                toast.error("Failed to retrieve user logo");
            }
        }
    };

    useEffect(() => {
        // guard: only fetch if currentUser has a valid token
        if (!currentUser?.token) return;
        getUserDetails();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.id]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        handleUpload(selectedFile);
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        setIsDragging(true);
    };

    const handleDragLeave = () => {
        setIsDragging(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        handleUpload(droppedFile);
    };

    const handleUpload = async (file) => {
        if (!file) {
            toast.error("Please select a file to upload.");
            return;
        }

        const allowedExtensions = ["jpg", "jpeg", "png"];
        const fileExtension = file.name.split(".").pop().toLowerCase();

        if (!allowedExtensions.includes(fileExtension)) {
            toast.error("Upload failed. Supported file types are: " + allowedExtensions.join(", "));
            return;
        }

        const formData = new FormData();
        formData.append("file", file);

        try {
            const response = await authFetch(
                `${apiUrl}/users/upload/${currentUser.id}`,
                {
                    method: 'POST',
                    body: formData
                },
                authParams
            );

            const responseData = await response.json();
            setUserLogo(responseData.fileUrl || null);
            toast.success("File uploaded successfully!");
        } catch (error) {
            if (!(error instanceof SessionExpiredError)) {
                console.error("Error uploading file:", error);
                toast.error("Failed to upload file.");
            }
        }
    };

    const handleDelete = async () => {
        try {
            await authFetch(
                `${apiUrl}/users/delete/${currentUser.id}`,
                { method: 'POST' },
                authParams
            );

            setUserLogo(null);
            toast.success("File deleted successfully!");
        } catch (error) {
            if (!(error instanceof SessionExpiredError)) {
                console.error("Error deleting file:", error);
                toast.error("Failed to delete file.");
            }
        }
    };

    return (
        <div
            className={`rounded-xl border bg-gray-50 p-4 flex flex-col items-center justify-center min-h-[160px] transition-all ${
                isDragging
                    ? "border-primary border-2 bg-primary/5"
                    : "border-gray-200 hover:border-gray-300"
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            {!userLogo ? (
                <>
                    <div className="p-2.5 bg-white rounded-full mb-2 shadow-sm">
                        <Upload className="w-5 h-5 text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-700 mb-0.5">Custom Logo</p>
                    <p className="text-xs text-gray-500 mb-3">
                        Drag and drop or click to upload (JPG, PNG)
                    </p>
                    <input
                        type="file"
                        className="hidden"
                        id="file-input"
                        onChange={handleFileChange}
                        accept=".jpg,.jpeg,.png"
                    />
                    <label
                        htmlFor="file-input"
                        className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-100 transition-colors shadow-sm"
                    >
                        Browse Files
                    </label>
                </>
            ) : (
                <div className="relative w-full h-full flex items-center justify-center">
                    <div className="rounded-lg p-3 flex items-center justify-center">
                        <img
                            src={userLogo}
                            alt="Logo Preview"
                            className="max-h-24 max-w-full object-contain"
                        />
                    </div>
                    <button
                        onClick={handleDelete}
                        className="absolute top-0 right-0 p-1.5 bg-white rounded-lg shadow-sm hover:bg-red-50 transition-colors border border-gray-200"
                        title="Delete logo"
                    >
                        <Trash2 className="w-4 h-4 text-red-500" />
                    </button>
                </div>
            )}
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    currentUser: selectCurrentUser,
});

const mapDispatchToProps = (dispatch) => ({
    onSessionExpired: () => dispatch(sessionExpired()),
    onTokenRefresh: (token) => dispatch(updateToken(token)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FileUpload);
